import {
  ChatCircleIcon,
  DotsThreeIcon,
  GlobeIcon,
  ShareIcon,
  ThumbsUpIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  FACEBOOK_FEED_FOLD_LENGTH,
  type PlatformPreviewBaseProps,
  compactNumber,
  previewTimestampLabel,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Facebook feed post — header (avatar + name + time + globe), text content,
 * optional photo, reaction count row, action bar (Like / Comment / Share).
 *
 * ── WHY THIS PAINTS IN FACEBOOK'S COLOURS AND NOT MORA'S ──────────────────
 * It used to use Mora's semantic tokens (`bg-card`, `text-foreground`,
 * `text-muted-foreground`), and `InstagramPost.tsx` still states that as
 * policy in its own header: _"NO platform-coloured chrome — uses semantic
 * tokens only."_ That is the exact opposite of `platform-tokens.ts`'s ruling
 * for the paid replicas — _"the platform's own appearance IS the
 * information"_ — and the two had been true at the same time since neither
 * file knew about the other.
 *
 * The founder settled it: the preview system's rule is that a preview looks
 * like the PLATFORM, not like Mora (`GOAL-AD-PREVIEW-SYSTEM-2026-08-21.md`
 * §2), and the organic family was named as the "comprehensive fix going
 * forward — same skins, same tokens, same stage."
 *
 * Worth knowing how small the delta actually is, because it is easy to
 * over-imagine: this file ALREADY carried Facebook's own blue on the
 * reaction badge, so "no platform-coloured chrome" was never true of it. What
 * changes is the card surface and the ink. In LIGHT mode Mora's `--card` is
 * 100% white and Facebook's card is `#FFFFFF` — identical, so nothing moves.
 * The real change is DARK, where Mora's card is zinc and Facebook's is
 * `#242527`, a warmer, lighter grey that is what Facebook actually renders.
 *
 * Deliberately NOT converted: `InstagramReel`, `InstagramStory`,
 * `TikTokVideo`, `YouTubeShort`. Those are full-bleed shells whose white
 * chrome sits on arbitrary media and must not follow any theme — each says so
 * in its own header, and that reasoning still holds.
 */
export const FacebookPost: React.FC<PlatformPreviewBaseProps> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  caption,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  timestamp,
  engagement,
  isLoading = false,
  mediaOverlay,
  className,
}) => {
  const time = previewTimestampLabel(timestamp);
  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(
    caption,
    FACEBOOK_FEED_FOLD_LENGTH,
  );
  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay, safeActive } =
    useCarousel(imageUrls, imageUrl, {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    });
  const hasMedia = !!currentImage || !!currentVideoUrl || !!videoUrl || isLoading;
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    1,
  );
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const shares = engagement?.shares;
  const hasEngagement = likes != null || comments != null || shares != null;
  // Reads the theme off `<html>` rather than via `useTheme()`, which throws
  // outside a ThemeProvider — and this replica renders in the style guide, in
  // unit tests, and inside the calendar's hover card.
  const isDarkMode = useResolvedDarkMode();
  const skin = PLATFORM_SKINS.facebook[isDarkMode ? 'dark' : 'light'];

  return (
    <article
      /*
        No outer border, matching `MetaAdPreview` — the paid sibling for this
        exact platform, whose Figma frames (`28:11226`/`28:11227`) carry no
        stroke on the root at all. A real Facebook feed card is separated from
        the page by the page's own colour, not by an outline. Two practical
        consequences of getting this wrong: `--platform-facebook-dark-card-border`
        is `#B0B3B8`, a TEXT grey, so using it as a border paints a bright
        outline around a dark card; and every container this renders in
        (`ARTIFACT_PREVIEW_CARD` in the inspector, the calendar's hover card,
        the gallery's stage) already supplies the edge. The container owns
        elevation — see the doubled-shadow note in `src/index.css`.
      */
      className={cn('w-full overflow-hidden rounded-lg', className)}
      data-platform="facebook-post"
      style={{ backgroundColor: skin.cardBackground }}
    >
      <header className="flex items-start justify-between gap-3 px-3 pt-3 pb-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-10 rounded-full"
            textClassName="text-caption font-semibold"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
              {brandName}
            </p>
            <div
              className="flex items-center gap-1 text-detail"
              style={{ color: skin.secondaryText }}
            >
              <span>{time}</span>
              <span aria-hidden="true">·</span>
              <GlobeIcon className="size-3" weight="fill" aria-label="Public" />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DotsThreeIcon className="size-5" weight="bold" style={{ color: skin.secondaryText }} />
        </div>
      </header>

      {caption ? (
        <div className="px-3 pb-3">
          <p
            className="whitespace-pre-wrap break-words text-sm leading-relaxed"
            style={{ color: skin.primaryText }}
          >
            {visible}
            {needsFold && !expanded ? (
              <>
                {'… '}
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="transition-opacity hover:underline hover:opacity-80"
                  style={{ color: skin.secondaryText }}
                >
                  See more
                </button>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {hasMedia ? (
        <MediaSlot
          ratio={resolvedAspect}
          bg="muted"
          imageUrl={currentImage}
          videoUrl={currentVideoUrl ?? videoUrl}
          videoPoster={currentVideoPoster ?? videoPoster}
          focalX={focalX}
          focalY={focalY}
          zoom={zoom}
          isLoading={isLoading}
          alt={caption ?? `${brandName} post`}
          overlay={
            mediaOverlay ? (
              <>
                {carouselOverlay}
                {mediaOverlay}
              </>
            ) : (
              carouselOverlay
            )
          }
        />
      ) : null}

      {hasEngagement ? (
        <div
          className="flex items-center justify-between border-b px-3 py-2 text-detail"
          /*
            The INTERNAL divider keeps `cardBorder`, which is what
            `MetaAdPreview` uses for the same line and what the Figma frames
            specify. Only the OUTER border was dropped — internal dividers are
            structural. (Worth a separate look someday: in dark that role
            resolves to `#B0B3B8`, which is bright for a hairline. If it is
            wrong it is wrong in both files, and should be changed in both with
            a sourced value, not silently diverged here.)
          */
          style={{ color: skin.secondaryText, borderColor: skin.cardBorder }}
        >
          {likes != null ? (
            <div className="flex items-center gap-1.5">
              <span className="size-4 rounded-full bg-platform-facebook flex items-center justify-center">
                {/* Platform-native badge: the icon renders white on Facebook's
                    own blue regardless of Mora's theme — the badge fill isn't
                    a Mora surface, so bg-background/text-foreground don't
                    apply. Same treatment used throughout this directory for
                    platform chrome (see MetaAdPreview.tsx's identical badge). */}
                {/* eslint-disable-next-line no-restricted-syntax -- see comment above */}
                <ThumbsUpIcon className="size-2.5 text-white" weight="fill" />
              </span>
              <span>{compactNumber(likes)}</span>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            {comments != null ? <span>{compactNumber(comments)} comments</span> : null}
            {shares != null ? <span>{compactNumber(shares)} shares</span> : null}
          </div>
        </div>
      ) : null}

      <FacebookActionBar textColor={skin.secondaryText} />
    </article>
  );
};

/**
 * `textColor` is optional so the existing standalone call sites keep working
 * unchanged; inside `FacebookPost` it is always the resolved skin's
 * secondary ink, which is what Facebook's own action bar uses.
 */
export const FacebookActionBar: React.FC<{ textColor?: string }> = ({ textColor }) => (
  <div className="grid grid-cols-3 px-1 py-0.5">
    <FbAction
      icon={<ThumbsUpIcon className="size-5" weight="regular" />}
      label="Like"
      textColor={textColor}
    />
    <FbAction
      icon={<ChatCircleIcon className="size-5" weight="regular" />}
      label="Comment"
      textColor={textColor}
    />
    <FbAction
      icon={<ShareIcon className="size-5" weight="regular" />}
      label="Share"
      textColor={textColor}
    />
  </div>
);

const FbAction: React.FC<{ icon: React.ReactNode; label: string; textColor?: string }> = ({
  icon,
  label,
  textColor,
}) => (
  <button
    type="button"
    className={cn(
      'flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium transition-colors',
      textColor ? undefined : 'text-foreground/70',
    )}
    style={textColor ? { color: textColor } : undefined}
  >
    {icon}
    <span>{label}</span>
  </button>
);
